import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface FeaturedPlayer {
  id: string;
  playerId: string;
  playerType: 'internal' | 'supabase';
  displayOrder: number;
  weekOf: string;
  username: string;
  firstName: string | null;
  lastName: string | null;
  profilePhotoUrl: string | null;
  sport: string | null;
  position: string | null;
  teamName: string | null;
  nominationReason: string | null;
}

interface UseOnTheComeUpOptions {
  stateProvince: string;
  country?: string;
  weekOf?: Date;
}

export const useOnTheComeUp = (options: UseOnTheComeUpOptions) => {
  const { stateProvince, country = 'United States', weekOf } = options;
  const [players, setPlayers] = useState<FeaturedPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Calculate week start (Monday)
  const getWeekStart = (date: Date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    return d.toISOString().split('T')[0];
  };

  const currentWeekStart = weekOf ? getWeekStart(weekOf) : getWeekStart(new Date());

  const fetchSelections = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch selections for the current week and state
      const { data: selectionsData, error: selectionsError } = await supabase
        .from('on_the_come_up_selections')
        .select(`
          id,
          player_id,
          player_type,
          display_order,
          week_of,
          nomination_id,
          on_the_come_up_nominations(nomination_reason)
        `)
        .eq('state_province', stateProvince)
        .eq('country', country)
        .eq('week_of', currentWeekStart)
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (selectionsError) throw selectionsError;

      if (!selectionsData || selectionsData.length === 0) {
        setPlayers([]);
        setLoading(false);
        return;
      }

      // Separate by player type
      const internalPlayerIds = selectionsData
        .filter(s => s.player_type === 'internal')
        .map(s => s.player_id);
      const supabasePlayerIds = selectionsData
        .filter(s => s.player_type === 'supabase')
        .map(s => s.player_id);

      // Fetch internal users
      let internalUsers: any[] = [];
      if (internalPlayerIds.length > 0) {
        const { data } = await supabase
          .from('internal_users')
          .select('id, username')
          .in('id', internalPlayerIds);
        internalUsers = data || [];
      }

      // Fetch internal profiles
      let internalProfiles: any[] = [];
      if (internalPlayerIds.length > 0) {
        const { data } = await supabase
          .from('internal_user_profiles')
          .select('user_id, first_name, last_name, profile_photo_url, sport, position')
          .in('user_id', internalPlayerIds);
        internalProfiles = data || [];
      }

      // Fetch supabase profiles
      let supabaseProfiles: any[] = [];
      if (supabasePlayerIds.length > 0) {
        const { data } = await supabase
          .from('profiles')
          .select('id, username, first_name, last_name, avatar_url, sport_type, position')
          .in('id', supabasePlayerIds);
        supabaseProfiles = data || [];
      }

      // Build maps
      const internalUserMap = new Map(internalUsers.map(u => [u.id, u]));
      const internalProfileMap = new Map(internalProfiles.map(p => [p.user_id, p]));
      const supabaseProfileMap = new Map(supabaseProfiles.map(p => [p.id, p]));

      // Enrich selections
      const enrichedPlayers: FeaturedPlayer[] = selectionsData.map(selection => {
        const isInternal = selection.player_type === 'internal';
        const nomination = selection.on_the_come_up_nominations;

        if (isInternal) {
          const user = internalUserMap.get(selection.player_id);
          const profile = internalProfileMap.get(selection.player_id);
          return {
            id: selection.id,
            playerId: selection.player_id,
            playerType: 'internal' as const,
            displayOrder: selection.display_order,
            weekOf: selection.week_of,
            username: user?.username || 'Unknown',
            firstName: profile?.first_name || null,
            lastName: profile?.last_name || null,
            profilePhotoUrl: profile?.profile_photo_url || null,
            sport: profile?.sport || null,
            position: profile?.position || null,
            teamName: null, // Could fetch from team_players if needed
            nominationReason: nomination?.nomination_reason || null,
          };
        } else {
          const profile = supabaseProfileMap.get(selection.player_id);
          return {
            id: selection.id,
            playerId: selection.player_id,
            playerType: 'supabase' as const,
            displayOrder: selection.display_order,
            weekOf: selection.week_of,
            username: profile?.username || 'Unknown',
            firstName: profile?.first_name || null,
            lastName: profile?.last_name || null,
            profilePhotoUrl: profile?.avatar_url || null,
            sport: profile?.sport_type || null,
            position: profile?.position || null,
            teamName: null,
            nominationReason: nomination?.nomination_reason || null,
          };
        }
      });

      setPlayers(enrichedPlayers);
    } catch (err) {
      console.error('Error fetching On the Come Up selections:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch selections');
    } finally {
      setLoading(false);
    }
  }, [stateProvince, country, currentWeekStart]);

  useEffect(() => {
    fetchSelections();
  }, [fetchSelections]);

  return {
    players,
    loading,
    error,
    refresh: fetchSelections,
    weekStart: currentWeekStart,
  };
};
